import type { Portefeuille } from "../domaine/types";
import { analyserCsv } from "./csv";
import { ErreurDonnees } from "./erreurs";
import {
  construirePortefeuille,
  FICHIERS,
  type NomTable,
  type TablesCsv,
} from "./portefeuille-csv";
import type { OptionsChargement, SourcePortefeuille } from "./source";

export interface ConfigSourceCsvStatique {
  /** URL du dossier qui contient les CSV, avec la barre finale (`/donnees/`). */
  urlBase: string;
  /** Injectable pour les tests. */
  fetch?: typeof globalThis.fetch;
}

/**
 * Lit les CSV publiés par scripts/importer-donnees.mjs.
 *
 * Les cinq fichiers sont demandés en parallèle ; le premier échec interrompt le
 * chargement. Une réponse HTML est traitée comme un fichier absent : un serveur de
 * développement ou une application monopage répond souvent `index.html` avec un
 * statut 200 à la place d'un fichier manquant.
 */
export class SourceCsvStatique implements SourcePortefeuille {
  readonly description = "Fichiers CSV issus du pipeline du prototype (poc/data)";
  readonly #urlBase: string;
  readonly #fetch: typeof globalThis.fetch;

  constructor(config: ConfigSourceCsvStatique) {
    this.#urlBase = config.urlBase.endsWith("/") ? config.urlBase : `${config.urlBase}/`;
    this.#fetch = config.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async chargerPortefeuille(options: OptionsChargement = {}): Promise<Portefeuille> {
    const noms = Object.keys(FICHIERS) as NomTable[];
    const textes = await Promise.all(noms.map((nom) => this.#lire(FICHIERS[nom], options.signal)));
    const tables = Object.fromEntries(
      noms.map((nom, i) => [nom, analyserCsv(textes[i] ?? "")]),
    ) as TablesCsv;
    return construirePortefeuille(tables);
  }

  async #lire(fichier: string, signal: AbortSignal | undefined): Promise<string> {
    const url = this.#urlBase + fichier;
    let reponse: Response;
    try {
      reponse = await this.#fetch(url, signal ? { signal } : {});
    } catch (erreur) {
      if (signal?.aborted) throw erreur;
      throw new ErreurDonnees(
        "chargement",
        `Impossible de joindre la source de données (${fichier}).`,
        [],
        {
          cause: erreur,
        },
      );
    }
    if (!reponse.ok) {
      throw new ErreurDonnees(
        "chargement",
        `La source de données a répondu ${reponse.status} pour ${fichier}.`,
      );
    }
    if (reponse.headers.get("content-type")?.includes("text/html")) {
      throw new ErreurDonnees(
        "chargement",
        `${fichier} est introuvable (le serveur a renvoyé une page HTML). Lancer « npm run donnees:importer ».`,
      );
    }
    return reponse.text();
  }
}
